import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MyButton from '../../../../shared-components/button/Button';
import CheckBoxButtons from '../../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import CustomMediaUpload from '../../../../shared-components/custom-media-upload/CustomMediaUpload';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import deviceApi from '../../../../utils/api/device-hub';
// ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout, PR #1130/#1143).
// Re-enable by uncommenting this block once backend security endpoints/fields ship.
/*
import ModelSecurityFields from '../security/ModelSecurityFields';
import {
  ModelSecurityFormState,
  appendSecurityToFormData,
  buildModelSecurityPayload,
  getEmptyModelSecurityForm,
  isModelSecurityError,
  validateModelSecurityForm,
} from '../security/securityProfile';
*/


/** Allowed values for the model's diagnosis protocol types multi-select */
const DIAGNOSTIC_PROTOCOL_TYPE_OPTIONS = [
  { label: 'HTTPS', value: 'HTTPS' },
  { label: 'FTPS', value: 'FTPS' },
  { label: 'HTTP', value: 'HTTP' },
  { label: 'FTP', value: 'FTP' },
];

/** Allowed values for the model's firmware protocol types multi-select */
const FIRMWARE_PROTOCOL_TYPE_OPTIONS = [
  { label: 'HTTPS', value: 'HTTPS' },
  { label: 'FTPS', value: 'FTPS' },
  { label: 'HTTP', value: 'HTTP' },
  { label: 'FTP', value: 'FTP' },
];

interface AddModelDrawerProperties {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /**
   * Called after a model is successfully created.
   * @param brandId - The brand that the model was added under
   * @param modelId - The _id of the newly created model
   * @param models - Updated list of model options for the selected brand
   */
  onModelCreated: (
    brandId: string,
    modelId: string,
    models: { value: string; label: string }[],
  ) => void;
}

interface BrandsListInterface {
  companyName?: string;
  name?: string;
  _id: string;
  source?: string;
}

/**
 * Slide-in drawer modal for adding a new device model.
 * @param {object} properties - The properties object.
 * @param {boolean} properties.open - Whether the modal is open.
 * @param {Function} properties.onClose - Callback to close the modal.
 * @param {Function} properties.onModelCreated - Callback called after a model is successfully created.
 * @returns {React.ReactElement} The rendered modal component.
 */
function AddModelDrawer({ open, onClose, onModelCreated }: Readonly<AddModelDrawerProperties>) {
  const [modelName, setModelName] = useState('');
  const [brandSelect, setBrandSelect] = useState('');
  const [brandError, setBrandError] = useState('');
  const [modelNameError, setModelNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useTranslation('station');
  const [modelMedia, setModelMedia] = useState<File[]>([]);
  const [brandsList, setBrandsList] = useState<BrandsListInterface[]>([]);
  const [diagnosticProtocolTypes, setDiagnosisProtocolTypes] = useState<string[]>([]);
  const [firmwareProtocolTypes, setFirmwareProtocolTypes] = useState<string[]>([]);
  // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet. See imports above.
  /*
  const [security, setSecurity] = useState<ModelSecurityFormState>(getEmptyModelSecurityForm);
  const [securityError, setSecurityError] = useState('');
  */

  const brandOptions = useMemo(
    () =>
      brandsList.map((brand) => {
        const isGlobal = !!brand.source;
        return {
          value: brand._id,
          label: brand.name || brand.companyName || '',
          labelNode: (
            <div className="nd-brand-option">
              <span>{brand.name || brand.companyName || ''}</span>
              <span className={`nd-badge ${isGlobal ? 'global' : 'local'}`}>
                {isGlobal ? t('global') : t('local')}
              </span>
            </div>
          ),
        };
      }),
    [brandsList, t],
  );

  /**
   * Resets all form fields to their initial state.
   */
  const resetForm = () => {
    setModelName('');
    setBrandSelect('');
    setModelMedia([]);
    setDiagnosisProtocolTypes([]);
    setFirmwareProtocolTypes([]);
    // setSecurity(getEmptyModelSecurityForm()); — SECURITY PROFILES DISABLED
    // setSecurityError(''); — SECURITY PROFILES DISABLED
    setBrandError('');
    setModelNameError('');
    setShowFormErrors(false);
  };

  /**
   * Handles the change event for the media upload.
   * @param {string} name - The name of the input field.
   * @param {string | number | File[] | boolean} changeValue - The selected files or values.
   */
  const handleMediaChange = (name: string, changeValue: string | number | File[] | boolean) => {
    if (name === 'medias') {
      setModelMedia(changeValue as File[]);
    }
  };

  /**
   * Handles closing the modal and resetting the form.
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * Validates and submits the model creation form.
   */
  const handleSubmit = () => {
    setShowFormErrors(true);
    setBrandError('');
    setModelNameError('');
    // setSecurityError(''); — SECURITY PROFILES DISABLED

    if (!brandSelect) {
      return;
    }
    if (!modelName.trim()) {
      return;
    }
    if (diagnosticProtocolTypes.length === 0) {
      return;
    }
    if (firmwareProtocolTypes.length === 0) {
      return;
    }
    // ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only rollout,
    // PR #1130/#1143). Re-enable by uncommenting this block once backend security
    // endpoints/fields ship.
    /*
    if (Object.keys(validateModelSecurityForm(security)).length > 0) {
      return;
    }
    */

    // const securityPayload = buildModelSecurityPayload(security); — SECURITY PROFILES DISABLED

    setSubmitting(true);

    const hasFile = modelMedia.length > 0 && modelMedia[0] instanceof File;
    const hasStringUrl =
      modelMedia.length > 0 &&
      typeof modelMedia[0] === 'string' &&
      (modelMedia[0] as unknown as string).trim() !== '';

    const selectedBrandOption = brandOptions.find((b) => b.value === brandSelect);
    const selectedBrandItem = brandsList.find((b) => b._id === brandSelect);
    const selectedBrandName =
      selectedBrandOption?.label ||
      selectedBrandItem?.name ||
      brandSelect;

    let apiCall: Promise<any>;

    if (hasFile) {
      const formData = new FormData();
      formData.append('name', modelName.trim());
      formData.append('brandId', brandSelect);
      formData.append('brandName', selectedBrandName);
      formData.append('modelImage', modelMedia[0]);
      diagnosticProtocolTypes.forEach((protocol, index) => {
        formData.append(`diagnosticProtocolTypes[${index}]`, protocol);
      });
      firmwareProtocolTypes.forEach((protocol, index) => {
        formData.append(`firmwareProtocolTypes[${index}]`, protocol);
      });
      // appendSecurityToFormData(formData, securityPayload); — SECURITY PROFILES DISABLED
      apiCall = deviceApi.createModel(formData, true);
    } else {
      const payload: {
        name: string;
        brandId: string;
        brandName: string;
        imageUrl?: string;
        diagnosticProtocolTypes: string[];
        firmwareProtocolTypes: string[];
        // security?: Record<string, unknown>; — SECURITY PROFILES DISABLED
      } = {
        name: modelName.trim(),
        brandId: brandSelect,
        brandName: selectedBrandName,
        diagnosticProtocolTypes,
        firmwareProtocolTypes,
        // ...(securityPayload && { security: securityPayload }), — SECURITY PROFILES DISABLED
      };
      if (hasStringUrl) {
        payload.imageUrl = modelMedia[0] as unknown as string;
      }
      apiCall = deviceApi.createModel(payload);
    }

    apiCall
      .then((response) => {
        // Reload models for the selected brand and pass back to parent

        deviceApi
          .fetchModels({ brandName: selectedBrandName })
          .then((modelsResponse) => {
            const data = modelsResponse.data?.results || modelsResponse.data || [];
            const models = data.map((m: any) => ({ value: m._id, label: m.name }));

            const newModel = response.data?.results?.[0] || response.data || {};
            const modelId = newModel._id || newModel.id || '';

            const matched = !modelId
              ? models.find((m: any) => m.label.toLowerCase() === modelName.trim().toLowerCase())
              : null;

            onModelCreated(brandSelect, modelId || matched?.value || '', models);
          })
          .catch(() => {
            onModelCreated(brandSelect, '', []);
          });

        setShowSuccess(true);
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || error?.message || 'Failed to create model';
        const lowerMessage = message.toLowerCase();
        // if (isModelSecurityError(message)) { — SECURITY PROFILES DISABLED
        //   setSecurityError(message);
        // } else
        if (
          lowerMessage.includes('model') ||
          lowerMessage.includes('already exists') ||
          lowerMessage.includes('already taken')
        ) {
          setModelNameError(message);
        } else if (lowerMessage.includes('brand') && !lowerMessage.includes('model')) {
          setBrandError(message);
        } else {
          setModelNameError(message);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  /**
   * Function to fetch brands list.
   * @returns {Promise<void>} Resolves when brands are fetched.
   */
  const fetchBrandsList = async () => {
    try {
      const response = await deviceApi.fetchBrands({
        paginate: false,
        status: ['approved', 'pending'],
      });
      const data = response?.data?.results || response?.data || [];
      setBrandsList(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Failed to fetch brands:', error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchBrandsList();
    }
  }, [open]);

  return (
    <>
      <ModalBox
        open={open}
        title=""
        content={
          <div className="left-drawer-content">
            <div className="drawer-header">
              <h3 className="drawer-title">{t('addModel')}</h3>
              <p className="drawer-subtitle">{t('createModelForBrand')}</p>
            </div>
            <div className="drawer-body">
              <SelectField
                name="modelBrandSelect"
                inputLabel={t('brand')}
                options={brandOptions}
                placeholder={t('chooseBrand')}
                selectedValue={brandSelect}
                onChangeValue={(_, value) => {
                  setBrandSelect(value.toString());
                  if (brandError) setBrandError('');
                }}
                error={Boolean((showFormErrors && !brandSelect) || brandError)}
                errorMessage={brandError || t('brandRequired')}
                valueRequired
                enableSearch
                hideLabelNodeInSelected
              />
              <CustomInput
                type="text"
                name="newModelName"
                inputLabel={t('modelName')}
                placeholder={t('enterModelName')}
                value={modelName}
                onChangeValue={(_, value) => {
                  setModelName(value.toString());
                  if (modelNameError) setModelNameError('');
                }}
                error={Boolean((showFormErrors && !modelName.trim()) || modelNameError)}
                errorMessage={modelNameError || t('modelNameRequired')}
                valueRequired
                characterLimit={100}
              />
              <CustomMediaUpload
                inputLabel={t('uploadMedia')}
                acceptedFileType=".jpg,.jpeg,.png"
                placeholder={t('uploadMediaPlaceholder')}
                name="medias"
                selectedFiles={modelMedia}
                onChangeValue={handleMediaChange}
                tooltipText={t('mediaModelTooltip')}
                maxImageSizeMB={1}
                maxFileCount={1}
              />

              <CheckBoxButtons
                inputLabel={t('diagnosticProtocolTypes')}
                name="diagnosticProtocolTypes"
                list={DIAGNOSTIC_PROTOCOL_TYPE_OPTIONS}
                value={diagnosticProtocolTypes}
                onChangeValue={(_, value) => setDiagnosisProtocolTypes(value as string[])}
                valueRequired
                error={showFormErrors && diagnosticProtocolTypes.length === 0}
                errorMessage={t('diagnosticProtocolTypesRequired')}
              />
              <CheckBoxButtons
                inputLabel={t('firmwareProtocolTypes')}
                name="firmwareProtocolTypes"
                list={FIRMWARE_PROTOCOL_TYPE_OPTIONS}
                value={firmwareProtocolTypes}
                onChangeValue={(_, value) => setFirmwareProtocolTypes(value as string[])}
                valueRequired
                error={showFormErrors && firmwareProtocolTypes.length === 0}
                errorMessage={t('firmwareProtocolTypesRequired')}
              />
              {/* ⚠ SECURITY PROFILES DISABLED — backend not deployed yet (frontend-only
                  rollout, PR #1130/#1143). Re-enable by uncommenting this block once backend
                  security endpoints/fields ship.
              <ModelSecurityFields
                value={security}
                onChange={(next) => {
                  setSecurity(next);
                  if (securityError) setSecurityError('');
                }}
                showErrors={showFormErrors}
                serverError={securityError}
              /> */}
            </div>
            <div className="drawer-footer">
              <MyButton label={t('cancel')} buttonType="error" onClickFunc={handleClose} />
              <MyButton
                label={t('saveModel')}
                buttonType="submit"
                onClickFunc={handleSubmit}
                disableButton={submitting}
                loading={submitting}
              />
            </div>
          </div>
        }
        closeModal={handleClose}
        header={false}
        position="right"
        modalBoxAnimation="modalAnimateFromRight"
      />
      <ModalBox
        open={showSuccess}
        title=""
        content={
          <SuccessNotification
            title={t('modelAddedSuccessfully')}
            description={t('modelAddedSuccessfullyDescription')}
            onNotificationClose={() => {
              setShowSuccess(false);
              resetForm();
              onClose();
            }}
          />
        }
        closeModal={() => {
          setShowSuccess(false);
          resetForm();
          onClose();
        }}
        header={false}
        notification
      />
    </>
  );
}

export default AddModelDrawer;
